import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://pay.mojomoney.africa';
const API = `${BACKEND_URL}/api`;

const fmt = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return 'R0';
  return `R${n.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;
};

const tierLabel = (t) => ({
  T0: 'Anonymous',
  T1: 'Light KYC',
  T2: 'Full FICA',
  T3: 'Enhanced',
}[t] || t);

const KycTierBadge = ({ className = '' }) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/users/me/kyc-tier`);
        if (!cancelled) setState({ loading: false, data: r.data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e?.response?.status || 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.loading) {
    return (
      <div className={`bg-white/10 rounded-lg p-3 text-xs text-white/70 ${className}`} data-testid="kyc-tier-badge-loading">
        Loading verification status…
      </div>
    );
  }

  if (state.error || !state.data) {
    return null;  // silent failure — don't block the dashboard
  }

  const { tier, exempt, daily_limit_zar, daily_remaining_zar, per_txn_limit_zar } = state.data;

  if (exempt || !tier) {
    return null;  // merchants/agents: hide the badge
  }

  const canUpgrade = tier !== 'T3';

  return (
    <div
      className={`bg-white/10 rounded-lg p-3 flex items-center justify-between gap-3 ${className}`}
      data-testid="kyc-tier-badge"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Shield className="w-5 h-5 text-emerald-300 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-xs text-white/70">Verification</p>
          <p className="text-sm font-semibold" data-testid="kyc-tier-label">
            Tier {tier} — {tierLabel(tier)}
          </p>
          <p className="text-xs text-white/60" data-testid="kyc-tier-remaining">
            {fmt(daily_remaining_zar)} of {fmt(daily_limit_zar)} remaining today
            <span className="mx-1">·</span>
            max {fmt(per_txn_limit_zar)} per transaction
          </p>
        </div>
      </div>
      {canUpgrade && (
        <button
          onClick={() => navigate('/customer/kyc')}
          className="text-xs font-semibold bg-white/10 hover:bg-white/20 rounded-md px-3 py-2 flex items-center gap-1 flex-shrink-0"
          data-testid="kyc-tier-upgrade-btn"
        >
          Upgrade
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default KycTierBadge;
